/**
 * "Does one of these work?" — the final-round ask, sent to the hiring team
 * BEFORE anything is offered to the candidate.
 *
 * This used to happen only as a side effect of BOOKING, which is the wrong order:
 * TA needs the panel's agreement on a time before promising it to a candidate.
 * So it lives in the round's own section now, on the options the recruiter ticked
 * there, and the proposal to the candidate is a separate, later step.
 *
 * The recruiter chooses the OPTIONS, the AUDIENCE, the LANGUAGE and — since
 * Oct 2026 (Theresa) — the WORDS. Until then this modal composed and sent in one
 * request, so a recruiter approved a message they had never seen.
 *
 * ⚠️ The old invariant here read "composed on the proxy … never sent from here —
 * so it can only ever name a slot the server actually computed". That is no
 * longer true of the words, and deliberately: the same scoped exception the
 * nudge takes. What still holds is the half that carries the risk — WHO this
 * reaches is resolved on the proxy (@finn.com addresses only, or a cleaned
 * channel) and WHICH options are real still comes from the server's own freshly
 * solved chains. An edit can change the wording; it cannot conjure a slot or
 * reach a candidate.
 */
import { useEffect, useState } from 'react';
import { ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { askFinalRoundPanel, previewFinalRoundAsk } from '../../services/taFinalRoundApi';
import type { ChainOption, FinalRoundConfig, RoleBoard, SchedulingCandidate } from '../../types/taActionBoard';
import { COLORS, firstNameFromEmail, fmtSlotRange, fmtTime } from './format';
import { LanguageToggle, type DraftLanguage } from './LanguageToggle';
import { MAX_ASK_CHARS } from './askLimits';
import { ModalActions } from './ModalActions';
import { audienceReady, PanelRecipientsField, type PanelAudience } from './PanelRecipients';

/** The round template's own people — the default audience for the ask. */
export function panelPeople(config: FinalRoundConfig | null): string[] {
  if (!config) return [];
  // Include a recruiter check-out STAND-IN so the OOO cover gets the ask/agenda
  // like every other participant. The default host is the board owner, who is
  // the person sending this and needs no invite of their own.
  const checkoutStandIn =
    config.recruiterCheckout && config.recruiterCheckoutEmail ? [config.recruiterCheckoutEmail] : [];
  return [
    ...new Set(
      [config.finalInterviewer, config.barRaiser, ...(config.g2k || []), ...checkoutStandIn].filter(
        Boolean
      )
    ),
  ];
}

export function AskPanelBody({
  role,
  candidate,
  options,
  boardEmail,
  onClose,
}: {
  role: RoleBoard;
  /** Whose round this is — named in the message so the panel knows who they'd meet. */
  candidate: SchedulingCandidate | null;
  /** The chain options ticked in the section above. */
  options: ChainOption[];
  boardEmail: string;
  onClose: () => void;
}) {
  const suggested = panelPeople(role.finalRounds.config);
  const [audience, setAudience] = useState<PanelAudience>({ recipients: suggested, channel: '' });
  const [sending, setSending] = useState(false);
  /**
   * Which language the panel reads, seeded from the ROLE so the common case
   * costs no click. The tags describe the job advert and this goes to the
   * hiring team, so it is a default the recruiter can override — a stand-in
   * covering a German pipeline may not read German.
   *
   * No re-fetch and no `chosen`/`rendered` split here, unlike the panel ask and
   * the nudge: this modal shows no draft at all, so there is nothing to
   * re-draft and nothing that can go stale. See the route's own comment — a
   * preview is the obvious next thing to build.
   */
  const [chosen, setChosen] = useState<DraftLanguage | null>(null);
  const [rendered, setRendered] = useState<DraftLanguage | null>(null);
  const language = chosen ?? rendered ?? role.language ?? 'de';
  const [text, setText] = useState('');
  const [touched, setTouched] = useState(false);
  const [preview, setPreview] = useState<
    { phase: 'loading' } | { phase: 'ready' } | { phase: 'error'; message: string }
  >({ phase: 'loading' });
  /** Ticked options a fresh calendar read no longer offers, named by the proxy. */
  const [gone, setGone] = useState<string[]>([]);
  /** Bumped to force a fresh preview after a refused send. */
  const [refreshToken, setRefreshToken] = useState(0);
  /**
   * The box holds the PREVIOUS language's text while the new one is fetched.
   * Derived rather than a `setState` inside the effect, which lints as a
   * cascading render — the nudge and the panel ask avoid it the same way.
   */
  const redrafting = chosen !== null && rendered !== chosen;

  const starts = options.map((o) => o.start);
  const startsKey = starts.join(',');
  useEffect(() => {
    let cancelled = false;
    previewFinalRoundAsk({
      postingId: role.postingId,
      opportunityId: candidate?.opportunityId,
      boardEmail,
      starts,
      ...(chosen ? { language: chosen } : {}),
    })
      .then((r) => {
        if (cancelled) return;
        if (r.error) {
          setPreview({ phase: 'error', message: r.error });
          return;
        }
        setText(r.text || '');
        setTouched(false);
        setGone(r.missing || []);
        /*
          Display and staleness only — never a fetch dependency, or this re-runs.

          ⚠️ `r.language` ONLY: falling back to `chosen` would label an old
          proxy's tag-derived text as the one that was asked for, and the
          recruiter could then send the language they just switched away from
          (CodeRabbit, PR #576). Unreported means unswitched.
        */
        setRendered(r.language ?? null);
        setPreview({ phase: 'ready' });
      })
      .catch(() => {
        if (!cancelled) {
          setPreview({ phase: 'error', message: 'Could not build the message — try again in a moment.' });
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `starts` is covered by startsKey
  }, [role.postingId, candidate?.opportunityId, boardEmail, startsKey, chosen, refreshToken]);

  const send = () => {
    setSending(true);
    /*
      ⚠️ An EDITED message is sent against the options the PREVIEW used, not
      everything still ticked.

      Without this the 409 below is a dead end rather than a safeguard: an
      option already gone at preview time is reported as `missing` on every
      later solve too, so every edited send was refused, the re-preview wiped
      the edit, and the only way to send was to give up editing. The edit never
      named that option anyway — the preview had already left it out.
      (CodeRabbit, PR #576.)

      The refusal still covers what it was built for: an option that goes AFTER
      the preview, which the edit may well name.
    */
    const sendStarts = touched ? starts.filter((s) => !gone.includes(s)) : starts;
    askFinalRoundPanel({
      postingId: role.postingId,
      opportunityId: candidate?.opportunityId,
      boardEmail,
      starts: sendStarts,
      recipients: audience.channel ? undefined : audience.recipients,
      channel: audience.channel || undefined,
      language,
      /*
        ONLY when edited. Sending the previewed text unconditionally made an
        unedited send carry the FIRST solve's options, so an option that went
        in between was still named in the DM while the response said it had
        been left out (CodeRabbit, PR #576). With nothing sent, the proxy
        composes from its own fresh solve and the two cannot disagree.
      */
      ...(touched ? { text } : {}),
    })
      .then((res) => {
        if (res.staleOptions) {
          // The edit named an option this send had just re-solved away. Nothing
          // went out; re-read so the box holds a message that is true again.
          // Loading FIRST: the re-read replaces the box, so leaving it editable
          // meanwhile loses whatever is typed and invites a second 409
          // (CodeRabbit, PR #576).
          setPreview({ phase: 'loading' });
          setRefreshToken((n) => n + 1);
          showToast({ status: 'warning', description: res.error });
          return;
        }
        if (res.error) {
          showToast({ status: 'error', description: res.error });
          return;
        }
        /**
         * Reported part by part, and NEVER as a plain success when something
         * was left out: a message naming two of the three options the recruiter
         * ticked has the panel replying about a time nobody offered them.
         */
        const failed = res.failed?.length
          ? ` ${res.failed.length} could not be reached on Slack — ${res.failed.join(', ')}.`
          : '';
        /*
          Everything the panel was NOT asked about: what this send re-solved
          away, PLUS what the preview had already dropped and an edited send
          therefore left out. Counting only `res.missing` would report "3 of 3"
          while the message named two (CodeRabbit, PR #576).
        */
        const left = new Set([...(res.missing ?? []), ...(touched ? gone : [])]);
        const asked = options.length - left.size;
        const missing = left.size
          ? ` ${left.size} ticked option${left.size === 1 ? '' : 's'} could NOT be included — a fresh calendar read no longer offers ${left.size === 1 ? 'it' : 'them'}. Refresh the role and ask about ${left.size === 1 ? 'it' : 'them'} again.`
          : '';
        const short = (res.failed?.length ?? 0) > 0 || left.size > 0;
        showToast({
          status: short ? 'warning' : 'success',
          description:
            `Asked ${res.sent?.length ?? 0} ${audience.channel ? 'channel' : 'panelist(s)'} about ` +
            `${asked} of ${options.length} option${options.length === 1 ? '' : 's'}.` +
            failed +
            missing,
        });
        onClose();
      })
      .catch((e: unknown) =>
        showToast({
          status: 'error',
          description: e instanceof Error ? e.message : 'Could not send the ask.',
        })
      )
      .finally(() => setSending(false));
  };

  return (
    <>
      <ModalHeader>
        <ModalTitle>Ask the panel — {role.roleTitle}</ModalTitle>
      </ModalHeader>
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Checks with the hiring team before anything reaches
          {candidate ? ` ${candidate.name}` : ' the candidate'}. The message names the agenda and, for
          each option, who would have to move a meeting.
        </p>

        <div className="space-y-2 rounded-xl border border-gray-200 p-3">
          {options.map((option, i) => (
            <OptionPreview key={option.start} option={option} index={i} />
          ))}
        </div>

        <PanelRecipientsField
          id="ask-panel-audience"
          suggested={suggested}
          value={audience}
          onChange={setAudience}
        />

        <div className="space-y-1.5">
          <label htmlFor="ask-panel-text" className="block text-sm font-medium text-gray-700">
            Message
          </label>
          <p className="text-xs text-gray-500">
            Edit it before sending — the same message goes to everyone above.
          </p>
          {preview.phase === 'error' ? (
            <p className="text-sm text-[#791F1F]">{preview.message}</p>
          ) : (
            <Textarea
              id="ask-panel-text"
              rows={12}
              value={preview.phase === 'loading' ? 'Building the message…' : text}
              // Locked while the switched-to message is in flight too: the
              // response REPLACES this box (CodeRabbit, PR #576).
              disabled={preview.phase === 'loading' || redrafting}
              onChange={(e) => {
                setTouched(true);
                setText(e.target.value);
              }}
              className="font-mono text-xs"
              maxLength={MAX_ASK_CHARS}
            />
          )}
          <p className="text-xs text-gray-500">
            Slack formatting: *bold*. @channel and @here are stripped before sending.
          </p>
          {redrafting && rendered === null && preview.phase === 'ready' && (
            <p className="text-xs text-[#791F1F]">
              This version of the server could not switch the language — the message above is
              unchanged. Reload the page and try again.
            </p>
          )}
          {gone.length > 0 && (
            <p className="text-xs text-[#791F1F]">
              {gone.length} ticked option{gone.length === 1 ? '' : 's'} could not be included — a
              fresh calendar read no longer offers {gone.length === 1 ? 'it' : 'them'}. Refresh the
              role to ask about {gone.length === 1 ? 'it' : 'them'} again.
            </p>
          )}
        </div>

        {/*
          Re-reads the whole message, so it is confirmed once the recruiter has
          typed — switching would otherwise discard their rewrite silently.
        */}
        <LanguageToggle
          value={language}
          onChange={setChosen}
          hasEdits={touched}
          disabled={sending || preview.phase === 'loading'}
          label="Panel reads"
        />
      </div>
      <ModalActions
        confirmLabel={sending ? 'Sending…' : `Ask about ${options.length} option${options.length === 1 ? '' : 's'}`}
        /*
          `redrafting` and the loading phase are in here because the box keeps
          the PREVIOUS language's text while the new one is fetched — confirming
          mid-switch would send the language just switched away from.
        */
        confirmDisabled={
          sending ||
          options.length === 0 ||
          !audienceReady(audience) ||
          preview.phase !== 'ready' ||
          redrafting ||
          !text.trim()
        }
        onConfirm={send}
        onCancel={onClose}
      />
    </>
  );
}

/** One option as the panel will read it: agenda per line, moves attributed. */
function OptionPreview({ option, index }: { option: ChainOption; index: number }) {
  return (
    <div className="text-xs">
      <p className="font-semibold text-gray-800">
        {index + 1}.{' '}
        <span
          role="img"
          aria-label={option.grade === 'free' ? 'Free' : 'Needs a meeting moved'}
          title={option.grade === 'free' ? 'Free' : 'Needs a meeting moved'}
          className="mr-1 inline-block h-2 w-2 rounded-full align-middle"
          style={{ backgroundColor: option.grade === 'free' ? COLORS.statusGreen : COLORS.statusOrange }}
        />
        {fmtSlotRange(option)}
      </p>
      <ul className="mt-0.5 space-y-0.5 pl-4 text-gray-600">
        {option.segments.map((segment) => {
          const moves = option.conflicts.filter((c) => c.segmentKey === segment.key);
          return (
            <li key={segment.key}>
              {fmtTime(segment.start)} {segment.label} (
              {segment.participants.map(firstNameFromEmail).join(', ')})
              {moves.length > 0 && (
                <span style={{ color: COLORS.orangeText }}>
                  {' '}
                  — {moves.map((c) => `${firstNameFromEmail(c.email)} moves “${c.title}”`).join('; ')}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
